import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Chip, LoadingState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import FlatEditDialog from "@/components/FlatEditDialog";
import AddResidentDialog from "@/components/AddResidentDialog";
import EditRelationshipDialog from "@/components/EditRelationshipDialog";
import { ArrowLeft, PencilSimple, Car, Users, Wallet, ChatCircleDots, Wrench,
    FileText, ArrowRight, Plus, Star, UserMinus, WarningCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { UTIL_MAP } from "@/lib/utilityTypes";
import { APPLIANCE_TYPE_MAP } from "@/lib/applianceTypes";
import { PARKING_TYPE_MAP } from "@/lib/parkingTypes";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");

const OPEN_COMPLAINT_STATUSES = new Set(["new", "acknowledged", "assigned", "in_progress", "on_hold", "reopened", "open"]);
const ACTIVE_WO_STATUSES = new Set(["OPEN", "ASSIGNED", "IN_PROGRESS", "ON_HOLD"]);

function titleCase(s) {
    return (s || "").toString().replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}
function statusVariant(status, activeSet) {
    if (["closed", "resolved", "completed", "paid", "CLOSED", "COMPLETED"].includes(status)) return "success";
    if (activeSet && activeSet.has(status)) return "warn";
    return "neutral";
}

function SummaryCard({ icon: Icon, label, value, testId }) {
    return (
        <div data-testid={testId} className="bg-white border border-brand-line rounded-sm p-5">
            <div className="flex items-center gap-2 text-brand-inkSoft">
                <Icon size={15} />
                <p className="text-[10px] uppercase tracking-overline">{label}</p>
            </div>
            <p className="font-heading text-2xl tracking-tight text-brand-ink mt-2">{value}</p>
        </div>
    );
}

function SectionHeader({ title, action }) {
    return (
        <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl text-brand-ink">{title}</h2>
            {action}
        </div>
    );
}

function ViewAllLink({ onClick, testId, children }) {
    return (
        <button data-testid={testId} onClick={onClick} className="text-xs text-brand-action hover:underline inline-flex items-center gap-1">
            {children} <ArrowRight size={12} />
        </button>
    );
}

function HouseholdGroup({ title, testId, residents, onSetPrimary, onEdit, onMoveOut, onView }) {
    return (
        <div className="bg-white border border-brand-line rounded-sm" data-testid={`flat-household-group-${testId}`}>
            <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft px-4 pt-3 pb-2">{title}</p>
            {residents?.length > 0 ? (
                <div className="divide-y divide-brand-line">
                    {residents.map((r) => (
                        <div key={r.relationship_id} className="p-4" data-testid={`flat-resident-${r.relationship_id}`}>
                            <div className="flex items-start justify-between gap-2">
                                <button onClick={() => onView(r.resident_id)} data-testid={`flat-resident-name-${r.relationship_id}`}
                                    className="text-sm font-medium text-brand-ink hover:text-brand-action hover:underline text-left">
                                    {r.name}
                                </button>
                                {r.is_primary_contact && (
                                    <Chip variant="success" data-testid={`flat-resident-primary-${r.relationship_id}`}><Star size={10} className="mr-1" />Primary</Chip>
                                )}
                            </div>
                            <p className="text-xs text-brand-inkSoft mt-1">{r.phone || "No phone on file"}{r.email && !r.email.endsWith("@no-email.maintyn.local") ? ` · ${r.email}` : ""}</p>
                            {r.move_in_date && <p className="text-xs text-brand-inkSoft mt-0.5">Moved in {fmtDate(r.move_in_date)}</p>}
                            <div className="flex items-center gap-3 mt-2">
                                {!r.is_primary_contact && (
                                    <button data-testid={`flat-set-primary-${r.relationship_id}`} onClick={() => onSetPrimary(r.relationship_id)}
                                        className="text-[11px] text-brand-action hover:underline inline-flex items-center gap-1"><Star size={11} /> Set Primary</button>
                                )}
                                <button data-testid={`flat-edit-relationship-${r.relationship_id}`} onClick={() => onEdit(r)}
                                    className="text-[11px] text-brand-inkSoft hover:text-brand-action hover:underline inline-flex items-center gap-1"><PencilSimple size={11} /> Edit Relationship</button>
                                <button data-testid={`flat-move-out-${r.relationship_id}`} onClick={() => onMoveOut(r)}
                                    className="text-[11px] text-brand-inkSoft hover:text-brand-action hover:underline inline-flex items-center gap-1"><UserMinus size={11} /> Mark Moved Out</button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : <p className="px-4 pb-4 text-sm text-brand-inkSoft">None on record.</p>}
        </div>
    );
}

export default function FlatProfile() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editOpen, setEditOpen] = useState(false);
    const [addResidentOpen, setAddResidentOpen] = useState(false);
    const [relDialog, setRelDialog] = useState({ open: false, mode: "edit", relationship: null });

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get(`/flats/${id}/profile`);
            setProfile(data);
        } catch (e) {
            toast.error(formatError(e));
            navigate("/app/flats");
        } finally { setLoading(false); }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    if (loading || !profile) return <LoadingState message="Loading flat profile..." />;

    const { flat, household, summary, finance, complaints, work_orders: workOrders, utility_connections: utilityConnections, appliances, parking_v2: parkingV2 } = profile;
    const flatLabel = `${flat.block}-${flat.number}`;

    async function setPrimary(relationshipId) {
        try {
            await api.patch(`/flat-residents/${relationshipId}`, { is_primary_contact: true });
            toast.success("Primary contact updated");
            load();
        } catch (e) { toast.error(formatError(e)); }
    }
    function openEditRelationship(r) { setRelDialog({ open: true, mode: "edit", relationship: r }); }
    function openMoveOut(r) { setRelDialog({ open: true, mode: "moveout", relationship: r }); }
    function viewResident(residentId) { navigate("/app/users", { state: { openResidentId: residentId } }); }
    async function viewApplianceInvoice(aid) {
        try {
            const res = await api.get(`/appliances/${aid}/invoice/download`, { responseType: "blob" });
            const url = window.URL.createObjectURL(res.data);
            window.open(url, "_blank");
        } catch (e) { toast.error(formatError(e)); }
    }

    return (
        <div className="animate-fade-up" data-testid="flat-profile-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
                <div>
                    <button data-testid="flat-profile-back-btn" onClick={() => navigate("/app/flats")}
                        className="text-xs text-brand-inkSoft hover:text-brand-action inline-flex items-center gap-1 mb-3">
                        <ArrowLeft size={13} /> Back to Flats
                    </button>
                    <h1 className="font-heading text-3xl lg:text-4xl tracking-tight text-brand-ink" data-testid="flat-profile-title">Flat {flatLabel}</h1>
                    <p className="text-brand-inkSoft mt-2">
                        {flat.floor ? `Floor ${flat.floor}` : "Floor —"} · {flat.bhk || "Type not set"} · <span className="capitalize">{flat.occupancy}</span>
                    </p>
                    <div className="mt-3">
                        <Chip data-testid="flat-profile-parking-chip" variant={flat.parking?.allotted ? "success" : "neutral"}>
                            <Car size={11} className="mr-1" />
                            {flat.parking?.allotted ? `Parking · ${flat.parking.slot_number}` : "No parking"}
                        </Chip>
                    </div>
                </div>
                <Button data-testid="flat-profile-edit-btn" onClick={() => setEditOpen(true)} variant="outline"
                    className="rounded-full border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-white shrink-0">
                    <PencilSimple size={15} className="mr-1.5" /> Edit Flat
                </Button>
            </div>

            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
                <SummaryCard icon={Users} label="Residents" value={summary.residents_count} testId="flat-summary-residents" />
                <SummaryCard icon={Car} label="Parking" value={summary.parking_allotted ? "Allotted" : "None"} testId="flat-summary-parking" />
                <SummaryCard icon={Wallet} label="Outstanding" value={inr(summary.outstanding)} testId="flat-summary-outstanding" />
                <SummaryCard icon={ChatCircleDots} label="Open Complaints" value={summary.open_complaints} testId="flat-summary-complaints" />
                <SummaryCard icon={Wrench} label="Active Work Orders" value={summary.active_work_orders} testId="flat-summary-workorders" />
            </div>

            {/* Residents & Household */}
            <section className="mb-10" data-testid="flat-section-household">
                <SectionHeader title="Residents & Household" action={
                    <div className="flex items-center gap-4">
                        <ViewAllLink testId="flat-view-directory-link" onClick={() => navigate("/app/directory")}>Resident directory</ViewAllLink>
                        <Button data-testid="add-resident-btn" size="sm" onClick={() => setAddResidentOpen(true)}
                            className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white h-8 px-3 text-xs">
                            <Plus size={13} className="mr-1" /> Add Resident
                        </Button>
                    </div>
                } />

                {household.warnings?.length > 0 && (
                    <div className="mb-4 border border-amber-300 bg-amber-50 rounded-sm p-3 flex items-start gap-2" data-testid="flat-household-warnings">
                        <WarningCircle size={16} className="text-amber-600 mt-0.5 shrink-0" />
                        <ul className="text-xs text-amber-800 space-y-1">
                            {household.warnings.map((w, i) => <li key={i}>{w}</li>)}
                        </ul>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <HouseholdGroup title="Owner" testId="owner" residents={household.active.owner}
                        onSetPrimary={setPrimary} onEdit={openEditRelationship} onMoveOut={openMoveOut} onView={viewResident} />
                    <HouseholdGroup title="Tenants" testId="tenant" residents={household.active.tenant}
                        onSetPrimary={setPrimary} onEdit={openEditRelationship} onMoveOut={openMoveOut} onView={viewResident} />
                    <HouseholdGroup title="Family / Other Occupants" testId="family" residents={[...household.active.family, ...household.active.other]}
                        onSetPrimary={setPrimary} onEdit={openEditRelationship} onMoveOut={openMoveOut} onView={viewResident} />
                </div>

                {household.history?.length > 0 && (
                    <details className="mt-4" data-testid="flat-household-history">
                        <summary className="cursor-pointer text-xs uppercase tracking-overline text-brand-inkSoft hover:text-brand-action">
                            Previous Residents / Household History ({household.history.length})
                        </summary>
                        <div className="mt-3 bg-white border border-brand-line rounded-sm divide-y divide-brand-line">
                            {household.history.map((h) => (
                                <div key={h.relationship_id} className="p-3 flex items-center justify-between flex-wrap gap-2" data-testid={`flat-history-${h.relationship_id}`}>
                                    <div>
                                        <p className="text-sm text-brand-ink">{h.name} <span className="text-brand-inkSoft text-xs ml-1">· {h.relationship_label}</span></p>
                                        <p className="text-xs text-brand-inkSoft mt-0.5">
                                            {h.move_in_date ? `Move-in: ${fmtDate(h.move_in_date)}` : ""}{h.move_in_date && h.move_out_date ? " · " : ""}{h.move_out_date ? `Move-out: ${fmtDate(h.move_out_date)}` : ""}
                                        </p>
                                    </div>
                                    <Chip variant="neutral">Moved Out</Chip>
                                </div>
                            ))}
                        </div>
                    </details>
                )}
            </section>

            {/* Parking */}
            <section className="mb-10" data-testid="flat-section-parking">
                <SectionHeader title="Parking" action={
                    user?.role !== "resident" && (
                        <ViewAllLink testId="flat-view-parking-link" onClick={() => navigate("/app/parking-management")}>Manage in Parking</ViewAllLink>
                    )
                } />
                {parkingV2?.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {parkingV2.map((p) => (
                            <div key={p.allocation_id} data-testid={`flat-parking-slot-${p.allocation_id}`} className="bg-white border border-brand-line rounded-sm p-4">
                                <div className="flex items-center justify-between">
                                    <p className="text-sm font-medium text-brand-ink">{p.slot_number}</p>
                                    <Chip variant={p.charging_enabled ? "warning" : "success"}>{p.charging_enabled ? "Paid" : "Free"}</Chip>
                                </div>
                                <p className="text-xs text-brand-inkSoft mt-1">
                                    {[PARKING_TYPE_MAP[p.parking_type]?.label, p.block, p.floor].filter(Boolean).join(" · ") || "Type not set"}
                                </p>
                                <p className="text-sm text-brand-ink mt-2">
                                    {p.vehicle ? `Vehicle: ${p.vehicle.registration_number}` : "No vehicle assigned"}
                                </p>
                                {p.pass ? (
                                    <p className="text-xs text-brand-inkSoft mt-1">Pass {p.pass.pass_number} · {p.pass.status}</p>
                                ) : <p className="text-xs text-brand-inkSoft mt-1">No parking pass issued</p>}
                                {p.charging_enabled && (
                                    <p className="text-xs text-brand-inkSoft mt-1">₹{p.charge_amount} / {p.frequency?.replace("_", " ")}</p>
                                )}
                            </div>
                        ))}
                    </div>
                ) : <div className="bg-white border border-dashed border-brand-line rounded-sm p-4 text-sm text-brand-inkSoft">No parking allocated to this flat.</div>}
            </section>

            {/* Utilities — read-only summary of the resident's tracked utility connections (BBPS foundation) */}
            <section className="mb-10" data-testid="flat-section-utilities">
                <SectionHeader title="Utilities" action={
                    <span data-testid="flat-utilities-count" className="text-xs text-brand-inkSoft">
                        {(utilityConnections?.length || 0)} connection{(utilityConnections?.length || 0) === 1 ? "" : "s"}
                    </span>
                } />
                {utilityConnections?.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {utilityConnections.map((c) => {
                            const u = UTIL_MAP[c.utility_type] || UTIL_MAP.other;
                            const Icon = u.icon;
                            return (
                                <div key={c.id} className="bg-white border border-brand-line rounded-sm p-4" data-testid={`flat-utility-${c.id}`}>
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-sm flex items-center justify-center shrink-0" style={{ background: u.color + "22", color: u.color }}>
                                            <Icon size={16} weight="duotone" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium text-brand-ink truncate">
                                                {c.nickname ? `${c.nickname} · ` : ""}{c.provider_name}
                                            </p>
                                            <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">{u.label}</p>
                                        </div>
                                    </div>
                                    <p className="text-xs text-brand-inkSoft mt-2 font-mono">
                                        {c.customer_id_label || "Customer ID"}: {c.customer_id_masked || c.customer_id}
                                    </p>
                                    <span className="inline-block mt-2 text-[10px] uppercase tracking-overline text-brand-inkSoft bg-brand-bg border border-brand-line rounded-full px-2 py-0.5">
                                        {c.integration_status === "READY" ? "Ready to connect" : "Not connected yet"}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                ) : <div className="bg-white border border-dashed border-brand-line rounded-sm p-4 text-sm text-brand-inkSoft">No utility connections tracked for this flat yet — residents can add them from My Flat.</div>}
            </section>

            {/* Appliances & AMC — read-only summary of the resident's tracked appliances */}
            <section className="mb-10" data-testid="flat-section-appliances">
                <SectionHeader title="Appliances & AMC" action={
                    <span data-testid="flat-appliances-count" className="text-xs text-brand-inkSoft">
                        {(appliances?.length || 0)} appliance{(appliances?.length || 0) === 1 ? "" : "s"}
                    </span>
                } />
                {appliances?.length > 0 ? (
                    <div className="bg-white border border-brand-line rounded-sm divide-y divide-brand-line">
                        {appliances.map((a) => {
                            const t = APPLIANCE_TYPE_MAP[a.appliance_type] || APPLIANCE_TYPE_MAP.other;
                            const Icon = t.icon;
                            return (
                                <div key={a.id} className="p-3 flex items-center justify-between flex-wrap gap-2" data-testid={`flat-appliance-${a.id}`}>
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="w-8 h-8 rounded-sm flex items-center justify-center shrink-0" style={{ background: t.color + "22", color: t.color }}>
                                            <Icon size={15} weight="duotone" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-sm text-brand-ink truncate">{a.brand || t.label} {a.model || ""}</p>
                                            <p className="text-xs text-brand-inkSoft">{t.label}{a.serial_number ? ` · S/N ${a.serial_number}` : ""}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <Chip variant={a.warranty_status === "active" ? "success" : a.warranty_status === "expired" ? "danger" : "neutral"}>
                                            Warranty {a.warranty_status === "active" ? "Active" : a.warranty_status === "expired" ? "Expired" : "Unknown"}
                                        </Chip>
                                        {a.amc_status !== "none" && (
                                            <Chip variant={a.amc_status === "active" ? "success" : "danger"}>AMC {a.amc_status === "active" ? "Active" : "Expired"}</Chip>
                                        )}
                                        {a.invoice_attached ? (
                                            <button data-testid={`flat-appliance-view-invoice-${a.id}`} onClick={() => viewApplianceInvoice(a.id)}
                                                className="text-xs text-brand-action hover:underline">View Invoice</button>
                                        ) : (
                                            <span className="text-xs text-brand-inkSoft">No invoice</span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : <div className="bg-white border border-dashed border-brand-line rounded-sm p-4 text-sm text-brand-inkSoft">No appliances tracked for this flat yet — residents can add them from My Flat.</div>}
            </section>

            {/* Finance */}
            <section className="mb-10" data-testid="flat-section-finance">
                <SectionHeader title="Finance" />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="bg-white border border-brand-line rounded-sm p-4">
                        <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Total billed</p>
                        <p className="font-heading text-xl text-brand-ink mt-1">{inr(finance.total_billed)}</p>
                    </div>
                    <div className="bg-white border border-brand-line rounded-sm p-4">
                        <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Total paid</p>
                        <p className="font-heading text-xl text-[#059669] mt-1">{inr(finance.total_paid)}</p>
                    </div>
                    <div className="bg-white border border-brand-line rounded-sm p-4">
                        <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Outstanding</p>
                        <p className="font-heading text-xl text-brand-action mt-1">{inr(finance.outstanding)}</p>
                    </div>
                </div>

                <p className="text-xs uppercase tracking-overline text-brand-inkSoft mb-2">Recent invoices</p>
                <div className="bg-white border border-brand-line rounded-sm divide-y divide-brand-line mb-4">
                    {finance.recent_invoices?.length > 0 ? finance.recent_invoices.map((inv) => (
                        <div key={inv.id} className="p-3 flex items-center justify-between flex-wrap gap-2" data-testid={`flat-invoice-${inv.id}`}>
                            <div>
                                <p className="text-sm text-brand-ink">{inv.description}</p>
                                <p className="text-xs text-brand-inkSoft">{inv.month} · {fmtDate(inv.created_at)}</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-brand-ink">{inr(inv.amount)}</span>
                                <Chip variant={statusVariant(inv.status)}>{titleCase(inv.status)}</Chip>
                                <button data-testid={`flat-invoice-view-${inv.id}`} onClick={() => navigate(`/app/invoices/${inv.id}`)} className="text-brand-action hover:underline text-xs">View</button>
                            </div>
                        </div>
                    )) : <p className="p-3 text-sm text-brand-inkSoft">No invoices yet.</p>}
                </div>

                <p className="text-xs uppercase tracking-overline text-brand-inkSoft mb-2">Recent payments</p>
                <div className="bg-white border border-brand-line rounded-sm divide-y divide-brand-line">
                    {finance.recent_payments?.length > 0 ? finance.recent_payments.map((p) => (
                        <div key={p.id} className="p-3 flex items-center justify-between flex-wrap gap-2" data-testid={`flat-payment-${p.id}`}>
                            <div>
                                <p className="text-sm text-brand-ink capitalize">{p.payment_mode}</p>
                                <p className="text-xs text-brand-inkSoft">{fmtDate(p.payment_date)}{p.reference_number ? ` · Ref: ${p.reference_number}` : ""}</p>
                            </div>
                            <span className="text-sm font-medium text-[#059669]">{inr(p.amount)}</span>
                        </div>
                    )) : <p className="p-3 text-sm text-brand-inkSoft">No payments recorded yet.</p>}
                </div>
            </section>

            {/* Complaints */}
            <section className="mb-10" data-testid="flat-section-complaints">
                <SectionHeader title="Complaints" action={
                    <ViewAllLink testId="flat-view-all-complaints" onClick={() => navigate("/app/complaints", { state: { prefillSearch: flatLabel } })}>View all complaints</ViewAllLink>
                } />
                <div className="bg-white border border-brand-line rounded-sm divide-y divide-brand-line">
                    {complaints.recent?.length > 0 ? complaints.recent.map((c) => (
                        <div key={c.id} className="p-3 flex items-center justify-between flex-wrap gap-2" data-testid={`flat-complaint-${c.id}`}>
                            <div>
                                <p className="text-sm text-brand-ink">{c.subject || c.title}</p>
                                <p className="text-xs text-brand-inkSoft">Created {fmtDate(c.created_at)}{c.updated_at ? ` · Updated ${fmtDate(c.updated_at)}` : ""}</p>
                            </div>
                            <div className="flex items-center gap-2">
                                {c.priority && <Chip variant="neutral">{titleCase(c.priority)}</Chip>}
                                <Chip variant={statusVariant(c.status, OPEN_COMPLAINT_STATUSES)}>{titleCase(c.status)}</Chip>
                            </div>
                        </div>
                    )) : <p className="p-3 text-sm text-brand-inkSoft">No complaints raised for this flat.</p>}
                </div>
            </section>

            {/* Work Orders */}
            <section className="mb-6" data-testid="flat-section-workorders">
                <SectionHeader title="Work Orders" action={
                    <ViewAllLink testId="flat-view-all-workorders" onClick={() => navigate("/app/work-orders", { state: { prefillSearch: flatLabel } })}>View all work orders</ViewAllLink>
                } />
                <div className="bg-white border border-brand-line rounded-sm divide-y divide-brand-line">
                    {workOrders.recent?.length > 0 ? workOrders.recent.map((w) => (
                        <div key={w.id} className="p-3 flex items-center justify-between flex-wrap gap-2" data-testid={`flat-wo-${w.id}`}>
                            <div>
                                <p className="text-sm text-brand-ink">{w.work_order_number} · {w.title}</p>
                                <p className="text-xs text-brand-inkSoft">
                                    {w.assigned_provider_name ? `Assigned to ${w.assigned_provider_name} · ` : "Unassigned · "}
                                    {w.approved_cost ? `${inr(w.approved_cost)} · ` : ""}
                                    Created {fmtDate(w.created_at)}{w.completed_at ? ` · Completed ${fmtDate(w.completed_at)}` : ""}
                                </p>
                            </div>
                            <Chip variant={statusVariant(w.status, ACTIVE_WO_STATUSES)}>{titleCase(w.status)}</Chip>
                        </div>
                    )) : <p className="p-3 text-sm text-brand-inkSoft">No work orders for this flat.</p>}
                </div>
            </section>

            {/* Documents — links to the existing shared Document Library (no flat-specific tagging yet) */}
            <section className="mb-6" data-testid="flat-section-documents">
                <SectionHeader title="Documents" action={
                    <ViewAllLink testId="flat-view-documents" onClick={() => navigate("/app/document-library")}>Document library</ViewAllLink>
                } />
                <div className="bg-white border border-dashed border-brand-line rounded-sm p-4 flex items-center gap-3 text-brand-inkSoft">
                    <FileText size={18} />
                    <p className="text-sm">Flat-specific document tagging isn't available yet — browse the shared Document Library for now.</p>
                </div>
            </section>

            <FlatEditDialog open={editOpen} onOpenChange={setEditOpen} editing={flat} onSaved={load} />
            <AddResidentDialog open={addResidentOpen} onOpenChange={setAddResidentOpen} flatId={flat.id} onSaved={load} />
            <EditRelationshipDialog open={relDialog.open} mode={relDialog.mode} relationship={relDialog.relationship}
                onOpenChange={(v) => setRelDialog((s) => ({ ...s, open: v }))} onSaved={load} />
        </div>
    );
}
